/**
 * Types et calculs partagés du module Facturation.
 *
 * Extraits de CreateInvoice.tsx (2017 lignes) sans aucun changement de comportement : ce sont
 * des fonctions pures, sans état ni rendu, utilisées aussi bien par le formulaire de saisie que
 * par le document de facture et l'export PDF.
 */

export type BillingSettings = {
    company_name?: string;
    logo_url?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    siret?: string;
    tva?: string;
    phone?: string;
    capital?: string;
    ape?: string;
    siege_social?: string;
    rcs_ville?: string;
    rcs_numero?: string;
    prestations_catalog?: string;
    tva_rates?: number[];
    enable_cover_count?: boolean;
    // Régime de TVA du vendeur (Paramètres > Facturation). En franchise en base, l'établissement n'a
    // pas le droit de facturer la TVA : les taux disponibles se réduisent à 0 % et la mention légale
    // correspondante est ajoutée automatiquement.
    tva_regime?: 'normal' | 'franchise_293b';
    tva_on_debits?: boolean | number;
    iban?: string;
    bic?: string;
    payment_delay_days?: number;
};

export type CatalogItem = {
    id: string;
    label: string;
    unit_price_ttc?: number;
    tax_rate?: number;
};

export type InvoiceLine = {
    id: string;
    label: string;
    quantity: number;
    ttcByRate: Record<number, number>;
};

export type CRMContactSuggestion = {
    id: string;
    type?: string;
    first_name?: string;
    last_name?: string;
    company_name?: string;
    organizer_name?: string;
    email?: string;
    phone?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
};

/** Une ligne enrichie de ses totaux, taux par taux. */
export type InvoiceLineSummary = InvoiceLine & {
    rateDetails: { rate: number; ttc: number; ht: number; tva: number; sourceAmount: number }[];
    totalHt: number;
    totalTva: number;
    totalTtc: number;
};

export const DEFAULT_ACCENT_COLOR = '#2f9e9e';

const clampChannel = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const rgbToHex = (r: number, g: number, b: number) => `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, '0')).join('')}`;

export const hexToRgba = (hex: string, alpha: number) => {
    const normalized = hex.replace('#', '').trim();
    const safeHex = normalized.length === 3
        ? normalized.split('').map((char) => char + char).join('')
        : normalized.padEnd(6, '0').slice(0, 6);
    const r = parseInt(safeHex.slice(0, 2), 16);
    const g = parseInt(safeHex.slice(2, 4), 16);
    const b = parseInt(safeHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Couleur d'accent du document, déduite du logo de l'établissement : moyenne pondérée des pixels,
 * en privilégiant les couleurs saturées et sombres (un logo est rarement de la couleur de son fond).
 */
export const extractLogoAccentColor = async (logoUrl?: string) => {
    if (!logoUrl) return DEFAULT_ACCENT_COLOR;

    return await new Promise<string>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = 64;
                canvas.height = 64;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(DEFAULT_ACCENT_COLOR);
                    return;
                }

                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

                let weightedR = 0;
                let weightedG = 0;
                let weightedB = 0;
                let totalWeight = 0;

                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    const a = data[i + 3];
                    if (a < 100) continue;

                    const max = Math.max(r, g, b);
                    const min = Math.min(r, g, b);
                    const brightness = (r + g + b) / 3;
                    const saturation = max === 0 ? 0 : (max - min) / max;

                    if (brightness > 245) continue;

                    const weight = Math.max(0.15, saturation * 2.2 + ((255 - brightness) / 255));
                    weightedR += r * weight;
                    weightedG += g * weight;
                    weightedB += b * weight;
                    totalWeight += weight;
                }

                if (totalWeight <= 0) {
                    resolve(DEFAULT_ACCENT_COLOR);
                    return;
                }

                const accent = rgbToHex(weightedR / totalWeight, weightedG / totalWeight, weightedB / totalWeight);
                resolve(accent);
            } catch {
                resolve(DEFAULT_ACCENT_COLOR);
            }
        };
        img.onerror = () => resolve(DEFAULT_ACCENT_COLOR);

        const src = String(logoUrl || '').trim();
        if (!src) {
            resolve(DEFAULT_ACCENT_COLOR);
            return;
        }

        img.src = src.startsWith('data:') ? src : `${src}${src.includes('?') ? '&' : '?'}accent=${Date.now()}`;
    });
};

export const toCurrency = (value: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(value) ? value : 0);

export const toInputDate = (date = new Date()) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export const toDisplayDate = (inputDate: string) => {
    if (!inputDate) return '--/--/----';
    const [y, m, d] = inputDate.split('-');
    return `${d}/${m}/${y}`;
};

const buildInvoiceClientPrefix = (companyName?: string, clientId?: string) => {
    const rawName = String(companyName || '').trim();
    if (!rawName) return 'FAC';

    const normalizedName = rawName
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .toUpperCase();

    const words = normalizedName.split(/\s+/).filter(w => w.length > 0);
    let namePart = '';

    if (words.length >= 2) {
        // Prend la première lettre de chaque mot (ex: "Hôtel de la Plage" -> "HDLP")
        namePart = words.map(w => w[0]).join('').slice(0, 5);
    } else {
        // Si un seul mot, prend les 4 premières lettres
        namePart = normalizedName.slice(0, 4);
    }

    return namePart || 'FAC';
};

export const computeDefaultInvoiceNumber = (existing: any[], companyName?: string, clientId?: string) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const ym = `${y}${m}`;
    const prefix = buildInvoiceClientPrefix(companyName, clientId);
    const regex = new RegExp(`^${prefix}-${ym}-(\\d{4})$`);
    const maxSeq = (Array.isArray(existing) ? existing : [])
        .map((f: any) => String(f?.invoice_number || ''))
        .map((n: string) => {
            const match = n.match(regex);
            return match ? Number(match[1]) : 0;
        })
        .reduce((acc: number, cur: number) => Math.max(acc, cur), 0);
    return `${prefix}-${ym}-${String(maxSeq + 1).padStart(4, '0')}`;
};

export const createLineId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const buildEmptyLine = (rates: number[], defaultLabel = ''): InvoiceLine => ({
    id: createLineId(),
    label: defaultLabel,
    quantity: 1,
    ttcByRate: Object.fromEntries(rates.map((rate) => [rate, 0])) as Record<number, number>,
});

export const normalizeLineForRates = (line: InvoiceLine, rates: number[]): InvoiceLine => ({
    ...line,
    ttcByRate: Object.fromEntries(rates.map((rate) => [rate, Number(line.ttcByRate?.[rate] ?? 0)])) as Record<number, number>,
});

export const getCRMDisplayName = (contact: CRMContactSuggestion) => {
    if (contact.type === 'PRIVÉ') {
        const privateName = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
        return privateName || contact.email || 'Client';
    }
    return contact.company_name || contact.organizer_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim() || 'Client';
};

// Prix unitaire HT et TTC de chaque ligne, déduits du total et de la quantité : la saisie se fait en
// TTC, mais la norme EN 16931 attend un prix unitaire HT (BT-146) et Emeni veut les deux visibles.
// Calculé ainsi plutôt que lu sur la ligne, car une facture saisie à la main ne porte pas de prix
// unitaire — seulement des montants par taux.
export const prixUnitaireHt = (line: { quantity: number; totalHt: number }) => {
    const quantite = Number(line.quantity) || 0;
    return quantite > 0 ? toCurrency(line.totalHt / quantite) : '-';
};

export const prixUnitaireTtc = (line: { quantity: number; totalTtc: number }) => {
    const quantite = Number(line.quantity) || 0;
    return quantite > 0 ? toCurrency(line.totalTtc / quantite) : '-';
};

/** Ce qu'on retrouve dans une facture enregistrée, prêt à être réinjecté dans le formulaire. */
export interface ParsedInvoice {
    snapshot: BillingSettings;
    rates: number[];
    values: Record<string, string>;
    amountMode: 'ttc' | 'ht';
    lines: InvoiceLine[];
}

/**
 * Relit une facture enregistrée (payload + instantané de facturation) pour la rouvrir en édition.
 * Séparé du composant parce qu'il n'y a là aucune interface : que de la lecture de données.
 */
export const parseInitialInvoice = (initialInvoice: any, fallbackRates: number[], fallbackLabel: string): ParsedInvoice => {
    const payload = typeof initialInvoice.payload_json === 'string'
        ? JSON.parse(initialInvoice.payload_json || '{}')
        : (initialInvoice.payload_json || {});

    const snapshot: BillingSettings = (() => {
        try {
            if (typeof initialInvoice.billing_snapshot === 'string') return JSON.parse(initialInvoice.billing_snapshot || '{}');
            return initialInvoice.billing_snapshot || payload.billing_snapshot || {};
        } catch {
            return payload.billing_snapshot || {};
        }
    })();

    const rates = Array.isArray(snapshot?.tva_rates) && snapshot.tva_rates.length > 0
        ? snapshot.tva_rates.map(Number)
        : fallbackRates;

    const values = {
        currentInvoiceId: String(initialInvoice.id || payload.id || ''),
        expectedPaymentMethod: String(payload.expectedPaymentMethod || ''),
        invoiceNumber: String(payload.invoiceNumber || initialInvoice.invoice_number || ''),
        invoiceDate: String(payload.invoiceDate || initialInvoice.due_date || toInputDate()),
        clientName: String(payload.clientName || initialInvoice.customer_name || ''),
        selectedCrmContactId: String(payload.crmContactId || payload.crm_contact_id || initialInvoice.crm_contact_id || ''),
        clientType: String(payload.clientType || ''),
        clientSiret: String(payload.clientSiret || ''),
        clientVatNumber: String(payload.clientVatNumber || ''),
        operationNature: String(payload.operationNature || 'services'),
        purchaseOrderRef: String(payload.purchaseOrderRef || ''),
        deliveryAddress: String(payload.deliveryAddress || ''),
        clientAddress: String(payload.clientAddress || ''),
        clientCity: String(payload.clientCity || ''),
        clientPostalCode: String(payload.clientPostalCode || ''),
        clientCountry: String(payload.clientCountry || 'France'),
        recipientEmail: String(payload.recipientEmail || ''),
        coverCount: String(payload.coverCount || ''),
        amountAlreadyPaid: String(payload.amountAlreadyPaid ?? initialInvoice.already_paid ?? ''),
    };

    let lines: InvoiceLine[] = Array.isArray(payload.lines) && payload.lines.length > 0
        ? payload.lines.map((line: any) => normalizeLineForRates({
            id: String(line.id || createLineId()),
            label: String(line.label || ''),
            quantity: Math.max(1, Number(line.quantity || 1)),
            ttcByRate: line.ttcByRate || Object.fromEntries(rates.map((rate: number) => [rate, 0])),
        }, rates))
        : [];

    // FALLBACK : Si on n'a pas de lignes ou si elles sont toutes à 0, mais qu'on a un montant global
    const globalAmount = Number(initialInvoice.total_ttc || initialInvoice.amount || payload.totalTtcBrut || 0);
    const hasRealContent = lines.some((l) => Object.values(l.ttcByRate || {}).some((v) => Number(v) > 0));

    if (!hasRealContent && globalAmount > 0) {
        const defaultRate = rates[0] || 20;
        lines = [{
            id: createLineId(),
            label: 'Prestation',
            quantity: 1,
            ttcByRate: { [defaultRate]: globalAmount } as Record<number, number>,
        }];
    } else if (lines.length === 0) {
        lines = [buildEmptyLine(rates, fallbackLabel)];
    }

    return { snapshot, rates, values, amountMode: payload.amountMode === 'ht' ? 'ht' : 'ttc', lines };
};
